import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Shield, 
  User, 
  Search,
  Filter,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  AlertCircle,
  KeyRound,
  Copy
} from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const queryClient = useQueryClient();

  // Password-reset modal state
  const [resetUser, setResetUser] = useState(null);      // user being reset
  const [resetMode, setResetMode] = useState('generate'); // 'generate' | 'manual'
  const [manualPassword, setManualPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState(null); // shown once after reset

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'STAFF',
    isActive: true,
    password: '',
    confirmPassword: ''
  });

  // Available roles
  const availableRoles = [
    { value: 'ADMIN', label: 'Administrator', description: 'Full system access' },
    { value: 'STAFF', label: 'Staff', description: 'Operational access' },
    { value: 'CUSTOMER', label: 'Customer', description: 'Limited access' }
  ];

  // The API returns roles nested: user.roles[0].role.code. Resolve it to a code
  // and a friendly, color-coded label (falls back to a prettified code).
  const getUserRoleCode = (u) => u?.roles?.[0]?.role?.code || u?.role || null;
  const roleLabel = (code) => {
    if (!code) return 'No role';
    const known = availableRoles.find((r) => r.value === code);
    if (known) return known.label;
    return code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const roleBadgeClass = (code) => ({
    ADMIN: 'bg-purple-100 text-purple-700',
    STAFF: 'bg-blue-100 text-blue-700',
    CUSTOMER: 'bg-gray-100 text-gray-700'
  }[code] || (code ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'));

  // Fetch users
  const { data: users, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/users`);
        return response.data.users || [];
      } catch (error) {
        console.error('Error fetching users:', error);
        return [];
      }
    }
  });

  // Add user mutation
  const addUserMutation = useMutation({
    mutationFn: async (userData) => {
      const response = await axios.post(`${API_BASE_URL}/api/admin/users`, userData);
      return response.data;
    },
    onSuccess: () => {
      toast.success('User created successfully');
      setShowAddForm(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create user');
    }
  });

  // Reset password mutation. Sends newPassword when the admin typed one;
  // otherwise the server generates a temporary password and returns it once.
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, newPassword }) => {
      const response = await axios.post(`${API_BASE_URL}/api/admin/users/${userId}/reset-password`, newPassword ? { newPassword } : {});
      return response.data;
    },
    onSuccess: (data) => {
      if (data.temporaryPassword) {
        setGeneratedPassword(data.temporaryPassword); // show once in the modal
      } else {
        toast.success('Password reset');
        closeResetModal();
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Failed to reset password');
    }
  });

  const openResetModal = (user) => {
    setResetUser(user);
    setResetMode('generate');
    setManualPassword('');
    setGeneratedPassword(null);
  };
  const closeResetModal = () => {
    setResetUser(null);
    setManualPassword('');
    setGeneratedPassword(null);
  };
  const submitReset = () => {
    if (resetMode === 'manual') {
      if (manualPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
      resetPasswordMutation.mutate({ userId: resetUser.id, newPassword: manualPassword });
    } else {
      resetPasswordMutation.mutate({ userId: resetUser.id });
    }
  };

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, userData }) => {
      const response = await axios.put(`${API_BASE_URL}/api/admin/users/${userId}`, userData);
      return response.data;
    },
    onSuccess: () => {
      toast.success('User updated successfully');
      setShowEditForm(false);
      setSelectedUser(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update user');
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId) => {
      await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}`);
    },
    onSuccess: () => {
      toast.success('User deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  });

  // Toggle user status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ userId, isActive }) => {
      const response = await axios.patch(`${API_BASE_URL}/api/admin/users/${userId}/status`, { isActive });
      return response.data;
    },
    onSuccess: () => {
      toast.success('User status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update user status');
    }
  });

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      role: 'STAFF',
      isActive: true,
      password: '',
      confirmPassword: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (showAddForm) {
      addUserMutation.mutate(formData);
    } else if (showEditForm && selectedUser) {
      const updateData = { ...formData };
      delete updateData.password;
      delete updateData.confirmPassword;
      updateUserMutation.mutate({ userId: selectedUser.id, userData: updateData });
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name || '',
      role: user.roles?.[0]?.role?.code || 'STAFF',
      isActive: user.isActive !== undefined ? user.isActive : true,
      password: '',
      confirmPassword: ''
    });
    setShowEditForm(true);
  };

  const handleDelete = (user) => {
    if (window.confirm(`Are you sure you want to delete ${user.email}?`)) {
      deleteUserMutation.mutate(user.id);
    }
  };

  const handleToggleStatus = (user) => {
    toggleStatusMutation.mutate({ userId: user.id, isActive: !user.isActive });
  };

  // Filter users
  const filteredUsers = users?.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const userRole = user.roles?.[0]?.role?.code;
    const matchesRole = roleFilter === 'all' || userRole === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.isActive === (statusFilter === 'active');
    
    return matchesSearch && matchesRole && matchesStatus;
  }) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-red-800 mb-2">Error Loading Users</h2>
        <p className="text-red-600">Failed to load user data. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-2">Manage user accounts, roles, and permissions</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          title="Add User"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Roles</option>
              {availableRoles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setRoleFilter('all');
                setStatusFilter('all');
              }}
              title="Clear Filters"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Users ({filteredUsers.length})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name || user.email}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Shield className="h-4 w-4 mr-2 text-gray-400" />
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${roleBadgeClass(getUserRoleCode(user))}`}>
                        {roleLabel(getUserRoleCode(user))}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.lastLoginAt 
                      ? new Date(user.lastLoginAt).toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(user)}
                        title="Edit"
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openResetModal(user)}
                        title="Reset password"
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <KeyRound className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(user)}
                        title={user.isActive ? 'Deactivate' : 'Activate'}
                        className={`${
                          user.isActive ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'
                        }`}
                      >
                        {user.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        title="Delete"
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset Password Modal */}
      {resetUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-24 mx-auto p-6 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="h-5 w-5 text-indigo-600" />
              <h3 className="text-lg font-medium text-gray-900">Reset password</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">For <span className="font-medium text-gray-700">{resetUser.email}</span></p>

            {generatedPassword ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm text-emerald-800 mb-2">Temporary password set. Share it with the user securely — it won’t be shown again.</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-white border border-emerald-200 px-3 py-2 font-mono text-sm text-gray-900 break-all">{generatedPassword}</code>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(generatedPassword); toast.success('Copied'); }}
                      title="Copy" className="p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button onClick={closeResetModal} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm">Done</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <button onClick={() => setResetMode('generate')}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${resetMode === 'generate' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                    Generate temporary
                  </button>
                  <button onClick={() => setResetMode('manual')}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${resetMode === 'manual' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                    Set specific
                  </button>
                </div>

                {resetMode === 'generate' ? (
                  <p className="text-sm text-gray-500">A strong temporary password will be generated and shown once for you to share. The user should change it after logging in.</p>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                    <input type="text" value={manualPassword} onChange={(e) => setManualPassword(e.target.value)}
                      placeholder="At least 6 characters" autoComplete="off"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" />
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={closeResetModal} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
                  <button onClick={submitReset} disabled={resetPasswordMutation.isPending}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm">
                    {resetPasswordMutation.isPending ? 'Resetting…' : 'Reset password'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit User Modal */}
      {(showAddForm || showEditForm) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {showAddForm ? 'Add New User' : 'Edit User'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  >
                    {availableRoles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </option>
                    ))}
                  </select>
                </div>
                
                {showAddForm && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </>
                )}
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Active Account
                  </label>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setShowEditForm(false);
                      setSelectedUser(null);
                      resetForm();
                    }}
                    title="Cancel"
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    title={showAddForm ? 'Create User' : 'Update User'}
                    disabled={addUserMutation.isPending || updateUserMutation.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {showAddForm ? 'Create User' : 'Update User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
